import { Alert, Box, Stack, Typography } from "@mui/material"
import { useMemo, useState } from "react"
import { Repeat } from "lib/@components/repeat"
import { EditField, NavigateTask } from "event-definitions"
import { MacroTokenValidationFailed } from "authoring/routes/macro/macro-events"
import { extractMacroTokensFromString } from "authoring/routes/macro/lib/macro-token-utils"
import { MacroTokenWarningBannerEntry } from "./macro-token-warning-banner-entry"

export function MacroTokenWarningBanner({ schedule, scopeId }) {
    const [tokens, setTokens] = useState([])
    const entries = useMemo(resolveEntries, [schedule, tokens])

    MacroTokenValidationFailed.useEvent(handleMacroTokenValidationFailed, [scopeId])

    if (!entries.length) return null

    return (
        <Alert severity="warning" sx={{ mb: 2 }}>
            <Stack spacing={1}>
                <Typography variant="subtitle2">Unrecognized macros in this draft</Typography>
                <Typography variant="body2" color="text.secondary">
                    Click a token to jump to it and fix or replace it.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Repeat
                        list={entries}
                        keyFn="token"
                        pass="entry"
                        item={<MacroTokenWarningBannerEntry onSelect={handleSelectEntry} />}
                    />
                </Box>
            </Stack>
        </Alert>
    )

    function handleMacroTokenValidationFailed(payload) {
        if (!payload) return
        if (payload?.scopeId && scopeId && payload.scopeId !== scopeId) return
        const nextTokens = resolveTokens(payload)
        setTokens(nextTokens)
    }

    function handleSelectEntry(entry) {
        if (!entry?.location) return
        focusTokenLocation(entry.location)
    }

    function resolveEntries() {
        return buildTokenEntries(schedule, tokens)
    }
}

function resolveTokens(payload) {
    if (Array.isArray(payload)) return sanitizeTokens(payload)
    if (Array.isArray(payload?.tokens)) return sanitizeTokens(payload.tokens)
    return []
}

function sanitizeTokens(tokens = []) {
    const cleaned = []
    const seen = new Set()
    for (const token of tokens) {
        if (!token || seen.has(token)) continue
        seen.add(token)
        cleaned.push(token)
    }
    return cleaned
}

function buildTokenEntries(schedule, tokens = []) {
    if (!schedule || !tokens.length) return []
    const occurrences = collectTokenOccurrences(schedule)
    const unknown = occurrences.filter(({ token }) => tokens.includes(token))
    const seen = new Set()
    const entries = []
    for (const occurrence of unknown) {
        if (seen.has(occurrence.token)) continue
        seen.add(occurrence.token)
        entries.push(occurrence)
    }
    return entries
}

function collectTokenOccurrences(schedule) {
    const entries = []
    if (!schedule) return entries

    collectTokens(
        schedule.title,
        {
            scope: "schedule",
            field: "title",
            target: schedule,
        },
        entries
    )

    collectTokens(
        schedule.intro?.content,
        {
            scope: "intro",
            field: "intro.content",
            target: schedule,
        },
        entries
    )

    collectTokens(
        schedule.intro?.note,
        {
            scope: "intro",
            field: "intro.note",
            target: schedule,
        },
        entries
    )

    for (let i = 0; i < (schedule.tasks ?? []).length; i += 1) {
        const task = schedule.tasks[i]
        collectTokens(
            task?.title,
            {
                scope: "task",
                field: "title",
                taskIndex: i,
                task,
            },
            entries
        )
        collectTokens(
            task?.content,
            {
                scope: "task",
                field: "content",
                taskIndex: i,
                task,
            },
            entries
        )
        collectTokens(
            task?.note,
            {
                scope: "task",
                field: "note",
                taskIndex: i,
                task,
            },
            entries
        )
    }

    return entries
}

function collectTokens(value, location, entries) {
    if (value === null || value === undefined) return
    for (const token of extractMacroTokensFromString(value ?? "")) {
        entries.push({ token, location })
    }
}

function focusTokenLocation(location) {
    if (!location) return
    if (location.scope === "schedule") {
        focusEditableText(location.target, "title", '[data-cy="schedule-title-input"]')
        return
    }
    if (location.scope === "intro") {
        focusRichTextBySelector(`[data-cy="editor-${location.field}"]`)
        return
    }
    if (location.scope === "task") {
        focusTaskLocation(location)
    }
}

function focusEditableText(target, field, selector) {
    EditField.raiseOnce({ target, field })
    const element = document.querySelector(selector)
    if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
        focusContentEditable(element)
    }
}

function focusTaskLocation({ task, taskIndex, field }) {
    if (!task) return
    const isDetailsField = field === "content" || field === "note"
    if (Number.isFinite(taskIndex)) {
        NavigateTask.raiseOnce(taskIndex, isDetailsField)
    }
    const taskSelector = buildTaskRowSelector(task?.id)
    const taskRow = taskSelector ? document.querySelector(taskSelector) : null
    if (taskRow) {
        taskRow.scrollIntoView({ behavior: "smooth", block: "center" })
    }
    if (field === "title") {
        EditField.raiseOnce({ target: task, field: "title" })
        return
    }
    if (isDetailsField) {
        setTimeout(() => {
            const row = taskRow ?? resolveActiveTaskRow()
            const editor = row?.querySelector(`[data-cy="editor-${field}"]`)
            if (editor) {
                focusContentEditable(editor)
            }
        }, 250)
    }
}

function buildTaskRowSelector(taskId) {
    if (!taskId) return ""
    const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(taskId) : taskId
    return `[data-cy="task-row-${escaped}"]`
}

function resolveActiveTaskRow() {
    return document.querySelector(".task.flash")
}

function focusRichTextBySelector(selector) {
    const editor = document.querySelector(selector)
    if (!editor) return
    editor.scrollIntoView({ behavior: "smooth", block: "center" })
    focusContentEditable(editor)
}

function focusContentEditable(container) {
    if (!container) return
    const editable = container.querySelector("[contenteditable='true']")
    if (editable) {
        editable.focus()
        return
    }
    if (container.focus) {
        container.focus()
    }
}
